/**
 * DashboardInsights — concentration and currency, as two small charts
 * ===================================================================
 * Usability review of 23 October 2026, item 13: *"Merge the Dashboard and
 * Analytics tabs. The Analytics tab is small (top exposures, currency, a
 * sensitivity number). Those work better as small charts on the Dashboard,
 * e.g. a concentration bar and a currency donut."*
 *
 * Concentration is by **issuer**, not by security row: DevvStream's note, its
 * promissory note and its common stock are one exposure (CLAUDE.md §0.32). A
 * row whose issuer nobody recorded stands under its own name, and the note
 * under the chart says how many did. Both charts are withheld, not scaled,
 * when any position has no dollar figure: a share of an incomplete total names
 * the wrong largest holding (§0.32).
 */
import React, { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";
import { Card } from "./ui";
import { InfoTip } from "./PageTools";
import { positionModel } from "./DashboardPositions";
import { number, usdValue } from "./positionRollup";
import { money, pct } from "./format";
import t from "./theme";

// Validated with the dataviz palette checker (lightness, chroma, colour-blind
// separation and contrast all pass on the card surface). Fixed order, never
// cycled: a fifth currency folds into "Other".
export const SERIES = ["#3a6fb0", "#c46a28", "#1f9470", "#9a5bb5"];
const OTHER = "#8A8078";
const BAR = "#3a6fb0";

const foldIssuer = (text) => String(text || "").toLowerCase().replace(/[.,]/g, "").replace(/\b(inc|corp|corporation|ltd|limited|ab|publ|plc|llc)\b/g, "").replace(/\s+/g, " ").trim();

/** The largest exposures by issuer, as a share of NAV. Null when incomplete. */
export function issuerConcentration(nav, top = 8) {
  const { positions } = positionModel(nav);
  if (!positions.length) return { rows: [], complete: true, unnamed: 0 };
  if (positions.some((p) => usdValue(p) === null)) return { rows: [], complete: false, unnamed: 0 };
  const total = number(nav?.summary?.nav);
  const groups = new Map();
  let unnamed = 0;
  for (const p of positions) {
    const issuer = (p.issuer || "").trim();
    if (!issuer) unnamed += 1;
    const label = issuer || p.name || p.ticker || "Unnamed";
    const key = foldIssuer(label);
    const row = groups.get(key) || { name: label, value: 0, count: 0 };
    row.value += usdValue(p);
    row.count += 1;
    groups.set(key, row);
  }
  const rows = [...groups.values()]
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .slice(0, top)
    .map((r) => ({ ...r, share: total ? (r.value / total) * 100 : null }));
  return { rows, complete: true, unnamed };
}

/** Positions plus cash, by currency, in dollars. Null when incomplete. */
export function currencySplit(nav) {
  const { positions, cash } = positionModel(nav);
  const by = new Map();
  let complete = true;
  for (const p of [...positions, ...cash]) {
    const value = p.isCash ? number(p.balance_usd) : usdValue(p);
    if (value === null) { complete = false; continue; }
    const ccy = p.currency || "Unknown";
    by.set(ccy, (by.get(ccy) || 0) + value);
  }
  const sorted = [...by].map(([name, value]) => ({ name, value })).sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const head = sorted.slice(0, SERIES.length - (sorted.length > SERIES.length ? 1 : 0));
  const rest = sorted.slice(head.length);
  const rows = rest.length ? [...head, { name: "Other", value: rest.reduce((s, r) => s + r.value, 0), other: true }] : head;
  const total = rows.reduce((s, r) => s + r.value, 0);
  return { rows: rows.map((r) => ({ ...r, share: total ? (r.value / total) * 100 : null })), complete, total };
}

function ChartTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="insight-tip">
      <strong>{row.name}</strong>
      <span>{money(row.value, { whole: true })}{row.share !== null && row.share !== undefined ? ` · ${pct(row.share)} ${row.count !== undefined ? "of NAV" : "of the total"}` : ""}</span>
      {row.count > 1 && <span>{row.count} positions</span>}
    </div>
  );
}

export function ConcentrationCard({ nav }) {
  const { rows, complete, unnamed } = issuerConcentration(nav);
  return (
    <Card title="Largest exposures by issuer" action={<InfoTip align="right">Every instrument of one company is one exposure — a note, its preferred and its common stock are added together. Bars are market value in dollars as a share of NAV.{unnamed ? ` ${unnamed} position${unnamed === 1 ? " has" : "s have"} no issuer recorded and stand under ${unnamed === 1 ? "its" : "their"} own name.` : ""}</InfoTip>}>
      {!complete ? (
        <p className="insight-note">Withheld: a position has no dollar value yet (an exchange rate is missing), so any share of NAV would be wrong.</p>
      ) : !rows.length ? (
        <p className="insight-note">No open positions.</p>
      ) : (
        <div className="insight-bars" style={{ height: rows.length * 30 + 10 }} role="img" aria-label={rows.map((r) => `${r.name} ${pct(r.share)} of NAV`).join(", ")}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 48, bottom: 0, left: 0 }} barCategoryGap={6}>
              <XAxis type="number" hide domain={[0, "dataMax"]} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fill: t.textSecondary, fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={(v) => (v.length > 24 ? `${v.slice(0, 23)}…` : v)} />
              <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(196,135,59,.08)" }} />
              <Bar dataKey={(r) => Math.abs(r.value)} fill={BAR} radius={[0, 4, 4, 0]} isAnimationActive={false}
                label={{ position: "right", fill: t.textSecondary, fontSize: 11, formatter: (v) => (rows[0] && number(nav?.summary?.nav) ? pct((v / number(nav.summary.nav)) * 100) : "") }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

export function CurrencyCard({ nav }) {
  const { rows, complete } = currencySplit(nav);
  const foreign = rows.filter((r) => r.name !== "USD" && !r.other && r.name !== "Unknown");
  const [shock, setShock] = useState(10);
  const [selected, setSelected] = useState("");
  const currency = foreign.find((c) => c.name === selected) || foreign[0];
  return (
    <Card title="Currency exposure" action={<InfoTip align="right">Positions plus cash, converted to dollars at the latest exchange rate. Accrued income is left out. The sensitivity line moves only the exchange rate: local prices, hedges and accrued income are held still.</InfoTip>}>
      {!complete ? (
        <p className="insight-note">Withheld: a position or balance has no dollar value yet, so the split would be wrong.</p>
      ) : !rows.length ? (
        <p className="insight-note">Nothing held.</p>
      ) : (
        <div className="insight-currency">
          <div className="insight-donut" role="img" aria-label={rows.map((r) => `${r.name} ${pct(r.share)}`).join(", ")}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rows} dataKey={(r) => Math.max(r.value, 0)} nameKey="name" innerRadius="58%" outerRadius="92%" paddingAngle={2} stroke="#fff" strokeWidth={2} isAnimationActive={false}>
                  {rows.map((r, i) => <Cell key={r.name} fill={r.other ? OTHER : SERIES[i % SERIES.length]} />)}
                </Pie>
                <Tooltip content={<ChartTip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="insight-legend">
            {rows.map((r, i) => (
              <li key={r.name}>
                <span className="insight-swatch" style={{ background: r.other ? OTHER : SERIES[i % SERIES.length] }} aria-hidden="true" />
                <span className="insight-legend-name">{r.name}</span>
                <span className="insight-legend-value">{pct(r.share)}</span>
                <span className="insight-legend-money">{money(r.value, { whole: true })}</span>
              </li>
            ))}
          </ul>
          {currency && (
            <div className="insight-sensitivity">
              <label>
                If{" "}
                {foreign.length > 1 ? (
                  <select value={currency.name} onChange={(e) => setSelected(e.target.value)} aria-label="Currency">
                    {foreign.map((c) => <option key={c.name}>{c.name}</option>)}
                  </select>
                ) : currency.name}{" "}
                falls <strong>{shock}%</strong> against the dollar:{" "}
                <strong className={currency.value > 0 ? "insight-neg" : ""}>{money(-currency.value * shock / 100, { whole: true })}</strong>
              </label>
              <input aria-label="Currency decline percent" type="range" min="0" max="25" value={shock} onChange={(e) => setShock(Number(e.target.value))} />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
